import { useEffect, useRef, useState } from 'react'
import { API_BASE } from '../../catalog/constants'
import { fetchWithPolicy } from '../../shared/api/fetchPolicy'
import { rankProducts } from './suggestions'
import type { SearchSuggestion } from './types'

export const useLiveSearchSuggestions = (query: string) => {
  const [liveLoading, setLiveLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([])
  const [suggestionsOpen, setSuggestionsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const requestIdRef = useRef(0)

  const trimmedQuery = query.trim()

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (trimmedQuery.length < 2) {
      setSuggestions([])
      setLiveLoading(false)
      setActiveIndex(-1)
      return
    }

    const currentRequestId = requestIdRef.current + 1
    requestIdRef.current = currentRequestId
    const controller = new AbortController()
    const timeout = window.setTimeout(async () => {
      setLiveLoading(true)
      try {
        const params = new URLSearchParams({
          status: 'active',
          search: trimmedQuery,
          page: '1',
          limit: '20',
          sortBy: 'updatedAt',
          sortOrder: 'desc',
        })
        const response = await fetchWithPolicy({
          request: `${API_BASE}/catalog/products?${params.toString()}`,
          init: {
            signal: controller.signal,
          },
          retries: 0,
        })
        if (!response.ok) {
          if (requestIdRef.current === currentRequestId) setSuggestions([])
          return
        }
        const payload = (await response.json()) as { products?: unknown[] }
        const products = Array.isArray(payload.products) ? payload.products : []
        const ranked = rankProducts(products, trimmedQuery).slice(0, 7)
        if (requestIdRef.current === currentRequestId) {
          setSuggestions(ranked)
          setSuggestionsOpen(true)
          setActiveIndex(-1)
        }
      } catch {
        if (requestIdRef.current === currentRequestId) setSuggestions([])
      } finally {
        if (requestIdRef.current === currentRequestId) setLiveLoading(false)
      }
    }, 220)

    return () => {
      window.clearTimeout(timeout)
      controller.abort()
    }
  }, [trimmedQuery])

  const closeSuggestions = () => {
    setSuggestionsOpen(false)
    setActiveIndex(-1)
  }

  const openSuggestions = () => {
    if (trimmedQuery.length < 2) return
    setSuggestionsOpen(true)
  }

  const moveSelection = (direction: 1 | -1) => {
    if (!suggestions.length) return
    setSuggestionsOpen(true)
    setActiveIndex((index) => {
      if (direction === 1) {
        return index >= suggestions.length - 1 ? 0 : index + 1
      }
      return index <= 0 ? suggestions.length - 1 : index - 1
    })
  }

  return {
    trimmedQuery,
    liveLoading,
    suggestions,
    suggestionsOpen,
    activeIndex,
    setActiveIndex,
    openSuggestions,
    closeSuggestions,
    moveSelection,
  }
}
